import { TeamAccess, TeamSupportDto } from '@api/ChallengeClient';
import { userSearchDebounce } from '@components/hooks/use-search';
import { useSearchReducer } from '@components/hooks/useSearchReducer';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import {
    CellProps,
    ReactColumn,
    SortingRule,
    TableComponent
} from '@components/table';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { getCdnImage } from '@components/utils/cdn-image';
import { Api } from '@core/api';
import { I18n } from '@core/i18n';
import { links } from '@core/router';
import { injectTSDI } from '@core/tsdi';
import { formatEnumLabel } from '@core/utils/format-enum-label';
import React from 'react';
import { useNavigate } from 'react-router';
import { Link, useParams } from 'react-router-dom';
import { DeleteTeamDialog } from '../../../teams/delete-team-dialog';

const accessBadgeVariant = (
    access: TeamAccess
): 'outline' | 'success' | 'warning' =>
    access === TeamAccess.OPEN
        ? 'success'
        : access === TeamAccess.REQUIRE_APPROVAL
          ? 'warning'
          : 'outline';

const CommunityTeamsContent = ({ communityId }: { communityId: number }) => {
    const navigate = useNavigate();
    const { challengeClient } = injectTSDI(Api);
    const { formatNumber } = injectTSDI(I18n);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [selectedTeam, setSelectedTeam] = React.useState<Pick<
        TeamSupportDto,
        'id' | 'name'
    > | null>(null);
    const [sorting, setSorting] = React.useState<
        SortingRule | SortingRule[] | undefined
    >(undefined);
    const [searchParams, dispatch] = React.useReducer(useSearchReducer, {
        size: 30,
        page: 0
    });
    const { setSearchTerm } = userSearchDebounce(dispatch);

    React.useEffect(() => {
        if (sorting) {
            dispatch({ type: 'sort', sort: sorting });
        }
    }, [sorting]);

    const teams = useWrapRequest(
        async (params?: QueryParamsDto) => {
            const result =
                await challengeClient.support.teamSupportControllerGetPage({
                    page: params?.page ?? 0,
                    size: params?.size ?? 30,
                    search: params?.search,
                    sort: params?.sort,
                    communityId
                });

            return {
                ...result.data,
                content: result.data.content || []
            };
        },
        {
            defaultData: {
                content: [],
                number: 0,
                size: 30,
                totalPages: 0,
                totalElements: 0
            }
        }
    );

    React.useEffect(() => {
        teams.request([searchParams]);
    }, [searchParams]);

    const refreshTeams = React.useCallback(() => {
        teams.request([searchParams], {
            stateLoading: false
        });
    }, [teams, searchParams]);

    const onSortingChange = (value?: SortingRule) => {
        if (value) {
            setSorting(value);
        }
    };

    const columns: ReactColumn<TeamSupportDto>[] = [
        {
            Header: 'Name',
            accessor: 'name',
            style: { width: 280 },
            Cell: ({
                row: {
                    original: { community, id, image, name }
                }
            }: CellProps<TeamSupportDto, string>) => (
                <div className="flex items-center gap-3">
                    <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted text-sm font-medium">
                        {image?.bucketName && image?.objectKey ? (
                            <img
                                src={getCdnImage(
                                    image.bucketName,
                                    image.objectKey,
                                    {
                                        resize: {
                                            width: 96,
                                            height: 96,
                                            fit: 'cover'
                                        }
                                    }
                                )}
                                alt={name}
                                className="size-full object-cover"
                            />
                        ) : (
                            name.charAt(0)
                        )}
                    </div>
                    <div className="min-w-0">
                        <Link
                            to={links.teamDetails(id.toString())}
                            state={{
                                communityId: community.id
                            }}
                            className="block truncate text-sm font-semibold hover:underline"
                        >
                            {name}
                        </Link>
                        <span className="text-xs text-muted-foreground">
                            Team ID: {id}
                        </span>
                    </div>
                </div>
            )
        },
        {
            Header: 'Access',
            accessor: 'access',
            style: { width: 160 },
            Cell: ({ value }: CellProps<TeamSupportDto, TeamAccess>) => (
                <Badge variant={accessBadgeVariant(value)}>
                    {formatEnumLabel(value)}
                </Badge>
            )
        },
        {
            Header: 'Member Count',
            accessor: 'memberCount',
            style: { width: 140 },
            Cell: ({ value }: CellProps<TeamSupportDto, number>) => (
                <span className="text-sm">{formatNumber(value)}</span>
            )
        }
    ];

    return (
        <>
            <div className="mb-4 flex flex-wrap justify-end gap-2">
                <Button
                    onClick={() =>
                        navigate(links.createCommunityTeam(String(communityId)))
                    }
                >
                    Create Team
                </Button>
                <Button
                    variant="outline"
                    onClick={() =>
                        navigate(
                            links.bulkCreateCommunityTeams(String(communityId))
                        )
                    }
                >
                    Create Multiple Teams
                </Button>
            </div>

            <TableComponent<TeamSupportDto[]>
                columns={columns}
                emptyText="No teams found"
                items={teams}
                searchPlaceholder="Search teams..."
                tableLayout="auto"
                onSortingChange={onSortingChange}
                sorting={Array.isArray(sorting) ? sorting[0] : sorting}
                onSearchChange={setSearchTerm}
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: 'View Details',
                        action: (team) => {
                            navigate(links.teamDetails(team.id.toString()), {
                                state: {
                                    communityId
                                }
                            });
                        }
                    },
                    {
                        label: 'Delete',
                        action: (team) => {
                            setSelectedTeam({
                                id: team.id,
                                name: team.name
                            });
                            setDeleteDialogOpen(true);
                        }
                    }
                ]}
            />

            <DeleteTeamDialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setSelectedTeam(null);
                }}
                team={selectedTeam}
                onSuccess={() => {
                    setDeleteDialogOpen(false);
                    setSelectedTeam(null);
                    refreshTeams();
                }}
            />
        </>
    );
};

const CommunityTeams = () => {
    const { communityId } = useParams();

    if (!communityId) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    Missing community identifier.
                </AlertDescription>
            </Alert>
        );
    }

    const parsedCommunityId = Number(communityId);

    if (Number.isNaN(parsedCommunityId)) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    Invalid community identifier.
                </AlertDescription>
            </Alert>
        );
    }

    return <CommunityTeamsContent communityId={parsedCommunityId} />;
};

export default CommunityTeams;
